import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Chip,
  Avatar,
  Divider,
  Button,
  Tabs, Tab, Badge,
  InputAdornment,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { cancelAppointment, decrementBookedAppointment, updateAudioCallStatus } from '../../service/appointment';

import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

import SearchIcon from '@mui/icons-material/Search';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideoCallIcon from '@mui/icons-material/VideoCall';
import { initiateIVRCall } from '../../service/ivr';


const appColor = (name) => {
  const n = name?.toLowerCase() || '';
  if (n === 'musicccare') return { bg: '#dcfce7', color: '#166534', border: '#86efac', subBg: '#f0fdf4' };
  if (n === 'echobuddy') return { bg: '#f3e8ff', color: '#7e22ce', border: '#d8b4fe', subBg: '#faf5ff' };
  if (n === 'echoshine') return { bg: '#eff6ff', color: '#2670d8', border: '#bfdbfe', subBg: '#f0f9ff' };
  if (n === 'echomindnest') return { bg: '#fef9c3', color: '#854d0e', border: '#fde047', subBg: '#fefce8' };
  return { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1', subBg: '#f8fafc' };
};

const AppointmentsTab = ({ activeAppointmentTab, filteredAppointments, setActiveAppointmentTab, todayCount, upcomingCount, draftCount, allAppointments, appointments, fetchAppointments, fetchTodayAppointments,
  fetchUpcomingAppointments, }) => {
  const navigate = useNavigate();

  const [callCooldowns, setCallCooldowns] = useState({});
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [cancelRemark, setCancelRemark] = useState('');
  const [search, setSearch] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [canCancel, setCanCancel] = useState(true);
  const [audioCallStatuses, setAudioCallStatuses] = useState({});
  const [callTimers, setCallTimers] = useState({});

  const handleVideoCallStatusChange = async (e, apt) => {
    e.stopPropagation();
    const value = e.target.value;
    if (!value) return;
    try {
      await updateAudioCallStatus(apt.id, value);
      setAudioCallStatuses(prev => ({ ...prev, [apt.id]: '' }));
      await fetchTodayAppointments();
      await fetchAppointments();
    } catch (err) {
      console.error('Failed to update video call status', err);
    }
  };

  const isMoreThan2HoursBefore = (startTime, slotDate) => {
    if (!startTime || !slotDate) return false;
    const now = new Date();
    const datePart = slotDate.split('T')[0];
    const appointmentDateTime = new Date(`${datePart}T${startTime}`);
    const timeDiff = appointmentDateTime - now;
    const twoHoursInMs = 2 * 60 * 60 * 1000;
    return timeDiff > twoHoursInMs;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCallCooldowns(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(id => {
          if (updated[id] > 0) {
            updated[id] -= 1;
          } else {
            delete updated[id];
          }
        });
        return updated;
      });
      setCallTimers(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(id => {
          if (updated[id] > 0) {
            updated[id] -= 1;
          } else {
            delete updated[id];
            // Timer expired — reset to call-initiated state
            setAudioCallStatuses(p => ({ ...p, [id]: '' }));
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const filterAppointments = (appointments) => {
    if (!search.trim()) return appointments;
    const searchTerm = search.toLowerCase().trim();
    return allAppointments.filter(apt => {
      const patientInfo = getPatientInfo(apt);
      return (
        patientInfo.name.toLowerCase().includes(searchTerm) ||
        patientInfo.id.toString().includes(searchTerm) ||
        (patientInfo.email && patientInfo.email.toLowerCase().includes(searchTerm)) ||
        apt.status?.toLowerCase().includes(searchTerm) ||
        apt.consultation_type?.toLowerCase().includes(searchTerm) ||
        formatDate(apt.slot_date).includes(searchTerm) ||
        formatTime(apt.start_time).includes(searchTerm)
      );
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const isCurrentTimeBetween = (slotDate, startTime, endTime, nowInput = null) => {
    const now = nowInput ? new Date(nowInput) : new Date();
    const [year, month, day] = slotDate.split('T')[0].split('-');
    const [startH, startM, startS = '0'] = startTime.split(':');
    const [endH, endM, endS = '0'] = endTime.split(':');
    const startDate = new Date(
      parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10),
      parseInt(startH, 10), parseInt(startM, 10), parseInt(startS, 10)
    );
    const endDate = new Date(
      parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10),
      parseInt(endH, 10), parseInt(endM, 10), parseInt(endS, 10)
    );
    return now >= startDate && now <= endDate;
  };

  const getPatientInfo = (apt) => {
    const name = apt.customer_name || 'Unknown';
    return {
      name,
      initials: name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
      id: apt.customer_id,
      email: apt.customer_email
    };
  };

  const handleViewClick = (patientInfo, appointment) => {
    if (!patientInfo?.id || !appointment?.id) return;
    navigate(`/patient/view-case/${patientInfo.id}/${appointment.id}`, {
      state: { patientInfo, appointment, mode: "view" },
    });
  };

  const handlePatientClick = (patientInfo, appointment) => {
    if (!patientInfo?.id || !appointment?.id) return;
    navigate(`/patient/view-case/${patientInfo.id}/${appointment.id}`, {
      state: { patientInfo, appointment, mode: "start-intake" },
    });
  };

  const handleVideoCall = (meetLink, e) => {
    e.stopPropagation();
    window.open(meetLink, '_blank');
  };

  const shouldShowVideoIcon = (apt) => {
    if (
      activeAppointmentTab !== 'today' ||
      apt.consultation_type !== 'video' ||
      !apt.counsellor_meet_link ||
      apt.status !== 'scheduled' ||
      !isToday(apt.slot_date)
    ) {
      return false;
    }
    const status = getTimeStatus(apt.start_time, apt.end_time);
    return status === 'before' || status === 'between';
  };

  const handleInitiateCall = async (e, apt) => {
    e.stopPropagation();
    try {
      setCallCooldowns(prev => ({ ...prev, [apt.id]: 30 }));
      const res = await initiateIVRCall(apt.doctor_id, apt.customer_id);
      console.log("IVR call response:", res);
    } catch (err) {
      console.error("IVR call failed", err);
      setCallCooldowns(prev => {
        const updated = { ...prev };
        delete updated[apt.id];
        return updated;
      });
    }
  };

  // Helper function to handle consultation click
  const handleConsultationClick = async (e, apt) => {
    e.stopPropagation();
    e.preventDefault();

    const type = apt.consultation_type?.trim().toLowerCase();
    if (type !== 'video' && !isModeEnabled(apt)) return;

    try {
      const type = apt.consultation_type?.trim().toLowerCase();

      if (type === "audio") {
        await initiateIVRCall(apt.doctor_id, apt.customer_id);
      }

      if (type === "video") {
        const meetUrl = apt.counsellor_meet_link || apt.meet_link;
        console.log("Opening meet URL:", meetUrl);
        if (!meetUrl) {
          alert("Meeting link not available for this appointment.");
          return;
        }
        const a = document.createElement("a");
        a.href = meetUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Failed to start consultation', err);
    }
  };

  // Check if mode is enabled (7 AM to end time)
  // const isModeEnabled = (apt) => {
  //   const now = new Date();
  //   const appointmentDate = apt.slot_date.split("T")[0];
  //   // const sevenAM = new Date(`${appointmentDate}T07:00:00`);
  //   const endTime = apt.end_time;
  //   const appointmentEnd = new Date(`${appointmentDate}T${endTime}:00`);

  //   return now <= appointmentEnd;
  //   // return now >= sevenAM && now <= appointmentEnd;
  // };

  const isModeEnabled = (apt) => {
    const now = new Date();

    const appointmentDate = apt.slot_date.split("T")[0];

    // Show from 8:00 AM on appointment day
    const visibleFrom = new Date(`${appointmentDate}T08:00:00`);

    // Hide after appointment end time
    const appointmentEnd = new Date(
      `${appointmentDate}T${apt.end_time}`
    );

    return now >= visibleFrom && now <= appointmentEnd;
  };

  const shouldShowModeChip = (apt) => {
    const status = apt.status?.toLowerCase();
    if (!isToday(apt.slot_date)) return false;
    if (status !== "scheduled" && status !== "drafted") return false;
    // Hide if session has ended — regardless of whether anyone joined
    const sessionEndDt = apt.slot_date && apt.end_time
      ? new Date(`${apt.slot_date.split('T')[0]}T${apt.end_time}`) : null;
    if (sessionEndDt && new Date() >= sessionEndDt) return false;
    // For video appointments show Join Session if link exists
    if (apt.consultation_type?.trim().toLowerCase() === "video") {
      return !!apt.counsellor_meet_link;
    }
    // For other types (audio), respect the time window
    return isModeEnabled(apt);
  };

  const handleAudioCallStatusChange = async (e, apt) => {
    e.stopPropagation();
    const value = e.target.value;
    if (!value) return;
    try {
      if (value === 'call-initiated') {
        // Only initiate IVR call and show ongoing on UI with 60s timer
        setCallCooldowns(prev => ({ ...prev, [apt.id]: 30 }));
        try {
          const res = await initiateIVRCall(apt.doctor_id, apt.customer_id);
          console.log("IVR call response:", res);
        } catch (err) {
          console.error("IVR call failed", err);
          setCallCooldowns(prev => {
            const updated = { ...prev };
            delete updated[apt.id];
            return updated;
          });
        }
        setAudioCallStatuses(prev => ({ ...prev, [apt.id]: 'call-initiated' }));
        setCallTimers(prev => ({ ...prev, [apt.id]: 60 }));
      } else {
        // "customer-not-joined" or "call-ended" — update backend and reset
        await updateAudioCallStatus(apt.id, value);
        setCallTimers(prev => {
          const updated = { ...prev };
          delete updated[apt.id];
          return updated;
        });
        setAudioCallStatuses(prev => ({ ...prev, [apt.id]: '' }));
        await fetchTodayAppointments();
        await fetchAppointments();
      }
    } catch (err) {
      console.error('Failed to update audio call status', err);
    }
  };

  const handleCancelClick = (appointment) => {
  const canCancel = isMoreThan2HoursBefore(
    appointment.start_time,
    appointment.slot_date
  );

  setAppointmentToCancel(appointment);
  setCanCancel(canCancel);
  setCancelDialogOpen(true);
};

  // const handleConfirmCancel = async () => {
  //   if (!appointmentToCancel) return;
  //   try {
  //     await cancelAppointment(appointmentToCancel.id);
  //     if (appointmentToCancel.customer_app_name?.toLowerCase() === 'musicccare') {
  //       await decrementBookedAppointment(appointmentToCancel.customer_id);
  //     }
  //     setCancelDialogOpen(false);
  //     setAppointmentToCancel(null);
  //     window.location.reload();
  //   } catch (error) {
  //     console.error('Failed to cancel appointment:', error);
  //     alert('Failed to cancel appointment. Please try again.');
  //   }
  // };

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return;

    try {
      if (isCancelling) return;

      setIsCancelling(true);
      await cancelAppointment(appointmentToCancel.id, cancelRemark, 'counsellor');

      if (
        appointmentToCancel.customer_app_name?.toLowerCase() === "musicccare"
      ) {
        await decrementBookedAppointment(appointmentToCancel.customer_id);
      }

      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      setCancelRemark('');

      // Refresh appointments without reloading page
      await fetchAppointments();
      await fetchTodayAppointments();
      await fetchUpcomingAppointments();


    } catch (error) {
      console.error("Failed to cancel appointment:", error);
      alert("Failed to cancel appointment. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setAppointmentToCancel(null);
    setCancelRemark('');
  };

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return { color: '#1d4ed8', backgroundColor: '#eff6ff', borderColor: '#bfdbfe' };
      case 'cancelled':
      case 'canceled':
        return { color: '#b91c1c', backgroundColor: '#fef2f2', borderColor: '#fecaca' };
      case 'completed':
        return { color: '#15803d', backgroundColor: '#f0fdf4', borderColor: '#bbf7d0' };
      case 'drafted':
        return { color: '#d97706', backgroundColor: '#fffbeb', borderColor: '#fde68a' };
      case 'not_attended':
        return { color: '#b91c1c', backgroundColor: '#fee2e2', borderColor: '#fecaca' };
      default:
        return { color: '#475569', backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' };
    }
  };

  const isToday = (dateStr) => {
    const today = new Date();
    const d = new Date(dateStr);
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };

  const isPastDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return d < today;
  };

  const getTimeStatus = (startTime, endTime) => {
    const now = new Date();
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const start = new Date();
    start.setHours(sh, sm, 0, 0);
    const end = new Date();
    end.setHours(eh, em, 0, 0);
    if (now < start) return 'before';
    if (now >= start && now <= end) return 'between';
    return 'after';
  };


  const getFilteredCounts = () => {
    if (!search.trim()) {
      return {
        filteredTodayCount: todayCount,
        filteredUpcomingCount: upcomingCount,
        filteredDraftCount: draftCount,
      };
    }
    const searchTerm = search.toLowerCase().trim();
    const filteredAllAppointments = allAppointments.filter(apt => {
      const patientInfo = getPatientInfo(apt);
      return (
        patientInfo.name.toLowerCase().includes(searchTerm) ||
        patientInfo.id.toString().includes(searchTerm) ||
        (patientInfo.email && patientInfo.email.toLowerCase().includes(searchTerm)) ||
        apt.status?.toLowerCase().includes(searchTerm) ||
        apt.consultation_type?.toLowerCase().includes(searchTerm) ||
        formatDate(apt.slot_date).includes(searchTerm) ||
        formatTime(apt.start_time).includes(searchTerm)
      );
    });
    const filteredTodayCount = filteredAllAppointments.filter(apt => isToday(apt.slot_date)).length;
    const filteredUpcomingCount = filteredAllAppointments.filter(apt => !isToday(apt.slot_date) && !isPastDate(apt.slot_date)).length;
    const filteredDraftCount = filteredAllAppointments.filter(apt => {
      const appointmentDateTime = new Date(
        `${apt.slot_date.split("T")[0]}T${apt.end_time}`
      );
      const diffInHours = (new Date() - appointmentDateTime) / (1000 * 60 * 60);
      const status = apt.status?.toLowerCase();
      if (!(diffInHours >= 0 && diffInHours <= 72 && (status === 'drafted' || status === 'scheduled'))) return false;
      if (status === 'cancelled' || status === 'canceled') return false;
      const ended = diffInHours >= 0;
      const hasActivity = !!(apt.customer_state?.trim() || apt.counsellor_state?.trim());
      if (status === 'scheduled' && !hasActivity && ended) return false;
      if (status === 'scheduled' && !apt.customer_state?.trim() && !!apt.counsellor_state?.trim()) return false;
      if (status === 'scheduled' && !!apt.customer_state?.trim() && !apt.counsellor_state?.trim()) return false;
      return true;
    }).length;

    return { filteredTodayCount, filteredUpcomingCount, filteredDraftCount };
  };

  const { filteredTodayCount, filteredUpcomingCount, filteredDraftCount } = getFilteredCounts();

  const isRowClickable = (apt) => {
    if (apt.consultation_type === 'view') return true;
    const now = new Date();
    if (apt.status === 'cancelled' || apt.status === 'canceled') return false;
    if (apt.status === 'scheduled') {
      const [startHours, startMinutes] = apt.start_time.split(':');
      const appointmentStart = new Date(apt.slot_date);
      appointmentStart.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
      if (now < appointmentStart) return false;
    }
    const [endHours, endMinutes] = apt.end_time.split(':');
    const appointmentEnd = new Date(apt.slot_date);
    appointmentEnd.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
    const hoursSinceEnd = (now - appointmentEnd) / (1000 * 60 * 60);
    if (hoursSinceEnd > 24) return false;
    return true;
  };

  return (
    <Box sx={{ mx: 'auto', background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)' }}>

      {/* Tabs + Search */}
      <Box sx={{ borderColor: "divider", mb: 2, display: "flex", justifyContent: "space-between", background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)' }}>
        <Tabs
          value={activeAppointmentTab}
          onChange={(_, newValue) => setActiveAppointmentTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.9rem", minHeight: 48 },
            "& .MuiTab-wrapper": { flexDirection: "row", gap: 1 },
          }}
        >
          <Tab
            value="today"
            icon={<CalendarMonthIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
            label={`Today (${filteredTodayCount})`}
          />
          <Tab
            value="upcoming"
            icon={<AccessTimeIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
            label={`Upcoming (${filteredUpcomingCount})`}
          />
          <Tab
            value="draft"
            icon={<AssignmentIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
            label={`Pending Review (${filteredDraftCount})`}
          />
        </Tabs>


        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search appointments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: { height: 36, '& input': { py: 0.5 } }
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, backgroundColor: 'background.paper' } }}
          />
        </Box>
      </Box>

      {/* Appointments List Card */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', bgcolor: 'white' }}>

        {(!search.trim() ? filteredAppointments : filterAppointments(filteredAppointments)).length === 0 ? (
          <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <CalendarMonthIcon sx={{ fontSize: 40, color: '#9ca3af' }} />
            </Box>
            <Typography sx={{ fontSize: '0.875rem', color: '#6b7280', mt: 0.5 }}>
              {search.trim() ? 'No appointments found matching your search' :
                activeAppointmentTab === 'today' ? 'No appointments for today' :
                  activeAppointmentTab === 'upcoming' ? 'No upcoming appointments' :
                    'No appointments found'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {(!search.trim()
              ? filteredAppointments.filter(
                apt => apt.status?.toLowerCase() !== "completed"
              )
              : filterAppointments(filteredAppointments).filter(
                apt => apt.status?.toLowerCase() !== "completed"
              )
            ).filter((apt) => {
              if (activeAppointmentTab !== 'draft') return true;
              const s = apt.status?.toLowerCase();
              if (s === 'cancelled' || s === 'canceled') return false;
              // exclude no-show variants
              const sessionEndDt = apt.slot_date && apt.end_time
                ? new Date(`${apt.slot_date.split('T')[0]}T${apt.end_time}`) : null;
              const ended = sessionEndDt && new Date() >= sessionEndDt;
              const hasActivity = !!(apt.customer_state?.trim() || apt.counsellor_state?.trim());
              if (s === 'scheduled' && !hasActivity && ended) return false;
              if (s === 'scheduled' && !apt.customer_state?.trim() && !!apt.counsellor_state?.trim()) return false;
              if (s === 'scheduled' && !!apt.customer_state?.trim() && !apt.counsellor_state?.trim()) return false;
              return true;
            }).map((apt, index) => {
              const patientInfo = getPatientInfo(apt);
              const isNewCase = apt.is_new_case;
              const isSlotToday = isToday(apt.slot_date);

              const timeStatus = isSlotToday ? getTimeStatus(apt.start_time, apt.end_time) : null;

              const isNotAttended =
                apt.status === 'scheduled' &&
                ((isSlotToday && timeStatus === 'after'));

              const isCancelledApt = apt.status?.toLowerCase() === 'cancelled' || apt.status?.toLowerCase() === 'canceled';

              // Session start/end time for no-show detection
              const sessionStartDt = apt.slot_date && apt.start_time
                ? new Date(`${apt.slot_date.split('T')[0]}T${apt.start_time}`)
                : null;
              const sessionEndDt = apt.slot_date && apt.end_time
                ? new Date(`${apt.slot_date.split('T')[0]}T${apt.end_time}`)
                : null;
              const aptSessionStarted = sessionStartDt && new Date() >= sessionStartDt;
              const aptSessionEnded = sessionEndDt && new Date() >= sessionEndDt;

              const hasMeetingActivity = !!(apt.customer_state?.trim() || apt.counsellor_state?.trim());
              const bothInSession = apt.customer_state === 'in-session' && apt.counsellor_state === 'in-session';
              const bothHaveActivity = !!(apt.customer_state?.trim() && apt.counsellor_state?.trim());
              const isMeetingInProgress = apt.status === 'scheduled' && bothInSession;
              const isPendingPatientUpdate = apt.status === 'scheduled' && bothHaveActivity && !bothInSession;
              const isNotAttendedBlank = apt.status === 'scheduled' && !hasMeetingActivity && aptSessionEnded;
              const isCustomerNotAttended = apt.status === 'scheduled' && !apt.customer_state?.trim() && !!apt.counsellor_state?.trim();
              const isCounsellorNotAttended = apt.status === 'scheduled' && !!apt.customer_state?.trim() && !apt.counsellor_state?.trim();

              const isAnyNoShow = aptSessionEnded && (isNotAttendedBlank || isCustomerNotAttended || isCounsellorNotAttended);

              const hoursAfterSession = sessionEndDt ? (new Date() - sessionEndDt) / (1000 * 60 * 60) : null;
              const isDraftedOver72h = apt.status?.toLowerCase() === 'drafted' && hoursAfterSession !== null && hoursAfterSession >= 72;
              const isDraftedUnder72h = apt.status?.toLowerCase() === 'drafted' && hoursAfterSession !== null && hoursAfterSession < 72;

              const resolveParticipantStatus = (state) => {
                if (aptSessionEnded && state?.toLowerCase() === 'in-session') return 'ended';
                if (state && state.trim() !== '' && state.toLowerCase() !== 'scheduled') return state;
                if (aptSessionEnded) return 'No Show';
                return state || null;
              };
              const customerStateDisplay   = resolveParticipantStatus(apt.customer_state);
              const counsellorStateDisplay = resolveParticipantStatus(apt.counsellor_state);
              const showParticipantRow = !isCancelledApt && (apt.customer_state?.trim() || apt.counsellor_state?.trim() || aptSessionEnded);

              // is_new_case === false → RETURNING (has medical history)
              // is_new_case === true  → NEW (no medical history)
              const customerStatus = !isNewCase ? 'Returning' : 'New';

              const appC = appColor(apt.customer_app_name);

              const displayList = !search.trim() ? filteredAppointments : filterAppointments(filteredAppointments);

              const displayAppointments = (!search.trim() ? filteredAppointments : filterAppointments(filteredAppointments))
                .filter(
                  apt =>
                    !(activeAppointmentTab === "today" &&
                      apt.status?.toLowerCase() === "completed")
                );
              return (
                <React.Fragment key={apt.id}>
                  <Box
                    onClick={isRowClickable(apt) ? () => handlePatientClick(patientInfo, apt) : undefined}
                    sx={{
                      px: 3,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'background-color 0.15s ease',
                      cursor: isRowClickable(apt) ? 'pointer' : 'default',
                      '&:hover': isRowClickable(apt) ? { bgcolor: '#f9fafb' } : {}
                    }}
                  >
                    {/* ── LEFT SIDE ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>

                        {/* Row 1: Avatar + ID + Name */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <Avatar variant="rounded" sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '4px',
                            bgcolor: patientInfo.initials.charCodeAt(0) % 3 === 0 ? '#d1fae5' : patientInfo.initials.charCodeAt(0) % 3 === 1 ? '#dbeafe' : '#fef3c7',
                            color: patientInfo.initials.charCodeAt(0) % 3 === 0 ? '#065f46' : patientInfo.initials.charCodeAt(0) % 3 === 1 ? '#1e40af' : '#92400e',
                            fontWeight: 600,
                            fontSize: '0.875rem'
                          }}>
                            {patientInfo.initials}
                          </Avatar>

                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              component="span"
                              sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#374151', fontFamily: 'monospace', letterSpacing: '0.02em' }}
                            >
                              {patientInfo.id}
                            </Typography>
                            <Typography sx={{ color: '#9ca3af', fontWeight: 500 }}>|</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#1a1a1a' }}>
                              {patientInfo.name}
                            </Typography>

                            {/* NEW / RETURNING — inline next to name */}
                            <Chip
                              label={customerStatus}
                              size="small"
                              sx={{
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                height: 20,
                                letterSpacing: '0.05em',
                                borderRadius: 1,
                                background: customerStatus?.toUpperCase() === 'NEW'
                                  ? 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)'
                                  : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                color: '#ffffff',
                                border: 'none',
                                boxShadow: customerStatus?.toUpperCase() === 'NEW'
                                  ? '0 2px 6px rgba(235, 215, 201, 0.4)'
                                  : '0 2px 6px rgba(206, 218, 236, 0.4)',
                                '& .MuiChip-label': { px: 1.25 },
                              }}
                            />
                          </Box>
                        </Box>

                        {/* Meta rows — 2 rows, 3 columns each */}
                        <Box sx={{ mt: 1, ml: 7, display: 'flex', flexDirection: 'column', gap: 0.6 }}>

                          {/* ── Row A: Appt ID · Meeting Slot · Type ── */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

                            {/* Appt ID */}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Appt ID</Typography>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#6d28d9' }}>#{apt.id}</Typography>
                            </Box>

                            <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />

                            {/* Meeting Slot */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Slot</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                                <CalendarTodayOutlinedIcon sx={{ fontSize: '0.72rem', color: '#64748b' }} />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#334155' }}>{formatDate(apt.slot_date)}</Typography>
                                <AccessTimeOutlinedIcon sx={{ fontSize: '0.72rem', color: '#64748b' }} />
                                <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, color: '#334155' }}>{formatTime(apt.start_time)} – {formatTime(apt.end_time)}</Typography>
                              </Box>
                            </Box>

                            <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />

                            {/* Consultation Type */}
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Type</Typography>
                              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: apt.consultation_type?.toLowerCase() === 'video' ? '#4f46e5' : '#0891b2' }}>
                                {apt.consultation_type?.replace(/\b\w/g, c => c.toUpperCase())}
                              </Typography>
                            </Box>

                          </Box>

                          {/* ── Row B: Platform · Status ── */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>

                            {/* App */}
                            {apt.customer_app_name && (
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>App</Typography>
                                <Box sx={{ px: 0.75, py: 0.1, bgcolor: appC.bg, borderRadius: '4px', border: `1px solid ${appC.border}` }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: appC.color, lineHeight: 1.6 }}>{apt.customer_app_name}</Typography>
                                </Box>
                              </Box>
                            )}

                            {apt.customer_app_name && apt.customer_account_name && apt.customer_account_name !== apt.customer_app_name && (
                              <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />
                            )}

                            {/* Account */}
                            {apt.customer_account_name && apt.customer_account_name !== apt.customer_app_name && (
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Account</Typography>
                                <Box sx={{ px: 0.75, py: 0.1, bgcolor: appC.subBg, borderRadius: '4px', border: `1px solid ${appC.border}` }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: appC.color, lineHeight: 1.6 }}>{apt.customer_account_name}</Typography>
                                </Box>
                              </Box>
                            )}

                            {(apt.customer_app_name || apt.customer_account_name) && <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />}

                            {/* Status — always shown */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</Typography>
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center',
                                px: 0.9, py: 0.15, borderRadius: '4px', border: '1px solid',
                                ...(isMeetingInProgress
                                  ? { bgcolor: '#dcfce7', borderColor: '#86efac', color: '#166534' }
                                  : isDraftedOver72h
                                  ? { bgcolor: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }
                                  : isPendingPatientUpdate || isDraftedUnder72h
                                  ? { bgcolor: '#fef9c3', borderColor: '#fde047', color: '#854d0e' }
                                  : isNotAttendedBlank
                                  ? { bgcolor: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }
                                  : isCustomerNotAttended
                                  ? { bgcolor: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }
                                  : isCounsellorNotAttended
                                  ? { bgcolor: '#fee2e2', borderColor: '#fca5a5', color: '#b91c1c' }
                                  : getStatusStyles(isNotAttended ? 'not_attended' : apt.status)),
                              }}>
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.6,
                                  color: isMeetingInProgress ? '#166534' : isDraftedOver72h ? '#b91c1c' : (isPendingPatientUpdate || isDraftedUnder72h) ? '#854d0e' : (isNotAttendedBlank || isCustomerNotAttended || isCounsellorNotAttended) ? '#b91c1c' : 'inherit'
                                }}>
                                  {isMeetingInProgress
                                    ? (apt.consultation_type?.trim().toLowerCase() === 'audio' ? 'Call Ongoing' : 'Meeting In Progress')
                                    : isPendingPatientUpdate
                                    ? 'Pending Patient Record Update'
                                    : isNotAttendedBlank
                                    ? 'No Show'
                                    : isCustomerNotAttended
                                    ? 'Customer No Show'
                                    : isCounsellorNotAttended
                                    ? 'Counsellor No Show'
                                    : isNotAttended
                                    ? 'No Show'
                                    : isDraftedUnder72h
                                    ? 'Pending Patient Record Update'
                                    : isDraftedOver72h
                                    ? 'Patient Record Not Updated'
                                    : apt.status?.replace(/\b\w/g, c => c.toUpperCase())}
                                </Typography>
                              </Box>
                            </Box>

                          </Box>

                          {/* ── Row C: Meeting Status (Customer · Counsellor) ── */}
                          {showParticipantRow && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Meeting Status</Typography>
                              <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Customer</Typography>
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center',
                                  px: 0.9, py: 0.15, borderRadius: '4px', border: '1px solid',
                                  bgcolor: customerStateDisplay === 'No Show' ? '#fee2e2' : '#f1f5f9',
                                  borderColor: customerStateDisplay === 'No Show' ? '#fca5a5' : '#e2e8f0',
                                }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.6, color: customerStateDisplay === 'No Show' ? '#b91c1c' : '#334155' }}>
                                    {customerStateDisplay ? customerStateDisplay.replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                                  </Typography>
                                </Box>
                              </Box>
                              <Box sx={{ width: '1px', height: 14, bgcolor: '#e2e8f0' }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Counsellor</Typography>
                                <Box sx={{
                                  display: 'inline-flex', alignItems: 'center',
                                  px: 0.9, py: 0.15, borderRadius: '4px', border: '1px solid',
                                  bgcolor: counsellorStateDisplay === 'No Show' ? '#fee2e2' : '#f1f5f9',
                                  borderColor: counsellorStateDisplay === 'No Show' ? '#fca5a5' : '#e2e8f0',
                                }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.6, color: counsellorStateDisplay === 'No Show' ? '#b91c1c' : '#334155' }}>
                                    {counsellorStateDisplay ? counsellorStateDisplay.replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}

                          {/* ── Row D: Cancellation info ── */}
                          {isCancelledApt && (
                            <Box sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', px: 1.25, py: 0.75, display: 'inline-flex', flexDirection: 'column', gap: 0.3, alignSelf: 'flex-start', maxWidth: '100%', overflow: 'hidden' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                {apt.cancellation_triggered_by ? (
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cancelled by</Typography>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#b91c1c' }}>
                                      {apt.cancellation_triggered_by.replace(/\b\w/g, c => c.toUpperCase())}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#b91c1c', fontStyle: 'italic' }}>
                                    Cancellation details not recorded
                                  </Typography>
                                )}
                                {apt.cancelled_at && (
                                  <>
                                    <Box sx={{ width: '1px', height: 12, bgcolor: '#fca5a5' }} />
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Cancelled at</Typography>
                                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#b91c1c' }}>
                                        {new Date(apt.cancelled_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                                      </Typography>
                                    </Box>
                                  </>
                                )}
                              </Box>
                              {apt.cancellation_remark ? (
                                <Box sx={{ display: 'block', width: '100%' }}>
    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.15, display: 'block', mb: 0.25 }}>Reason for cancellation</Typography>
                                  <Typography sx={{ fontSize: '0.72rem', color: '#374151', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap', display: 'block', width: '100%' }}>{apt.cancellation_remark}</Typography>
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontStyle: 'italic' }}>
                                  No cancellation remark provided
                                </Typography>
                              )}
                            </Box>
                          )}

                        </Box>
                      </Box>
                    </Box>


                    {/* ── RIGHT SIDE ── */}

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        minWidth: 180,
                      }}
                    >
                      {/* Video/Audio Call Icon */}
{shouldShowModeChip(apt) && (
  apt.consultation_type?.trim().toLowerCase() === 'audio' ? (() => {
    const isCallOngoing = apt.customer_state === 'in-session' && apt.counsellor_state === 'in-session';
    const isCallEnded = apt.counsellor_state === 'ended' && apt.customer_state === 'ended';
    const isCustomerNotJoined = apt.customer_state === 'No Show' && apt.counsellor_state === 'ended';
    const localStatus = audioCallStatuses[apt.id] || '';
    const showAsOngoing = isCallOngoing || localStatus === 'call-initiated';
    const timer = callTimers[apt.id] || 0;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <FormControl size="small" onClick={(e) => e.stopPropagation()}>
          <Select
            value={showAsOngoing ? 'call-ongoing' : ''}
            onChange={(e) => handleAudioCallStatusChange(e, apt)}
            displayEmpty
            disabled={false}
            sx={{
              height: 28,
              minWidth: 160,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '14px',
              color: '#fff',
              background: 'linear-gradient(135deg, #00b09b, #0ea5e9)',
              boxShadow: '0 2px 8px rgba(0, 176, 155, 0.3)',
              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              '.MuiSelect-icon': { color: '#fff' },
              '&:hover': {
                background: 'linear-gradient(135deg, #00967e, #0284c7)',
              },
            }}
          >
            <MenuItem value="" disabled>
              Select Status
            </MenuItem>
            {!showAsOngoing && <MenuItem value="call-initiated">Call Initiated</MenuItem>}
            {showAsOngoing && <MenuItem value="call-ongoing" disabled>Call Ongoing</MenuItem>}
            <MenuItem value="customer-not-joined">Customer Not Joined</MenuItem>
            <MenuItem value="call-ended">Call Ended</MenuItem>
          </Select>
        </FormControl>
        {timer > 0 && (
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#6b7280' }}>
            {timer}s
          </Typography>
        )}
      </Box>
    );
  })() : (
    <Chip
      label="Join Session"
      clickable
      disabled={!apt.counsellor_meet_link}
      onClick={(e) => handleConsultationClick(e, apt)}
      sx={{
        height: 28,
        borderRadius: "14px",
        px: 0.5,
        fontSize: "0.75rem",
        fontWeight: 600,
        color: "#fff",
        background: "linear-gradient(135deg, #00b09b, #0ea5e9)",
        boxShadow: "0 2px 8px rgba(0, 176, 155, 0.3)",
        transition: "all 0.2s ease",
        "&:hover": {
          background: "linear-gradient(135deg, #00967e, #0284c7)",
          transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0, 176, 155, 0.4)",
        },
        "& .MuiChip-label": {
          px: 1.2,
        },
        "&.Mui-disabled": {
          background: "#E5E7EB",
          color: "#9CA3AF",
          boxShadow: "none",
        },
      }}
    />
  )
)}


                      {/* VIEW/START INTAKE + Cancel — centered in the row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        {activeAppointmentTab !== 'upcoming' && !isCancelledApt && !isAnyNoShow && (
                          (apt.transaction_status === 0 || apt.transaction_status === '0' || !apt.transaction_status) ? (
                            <Chip
                              label="Patient Records"
                              onClick={(e) => { e.stopPropagation(); handleViewClick(patientInfo, apt); }}
                              size="small"
                              clickable
                              sx={{
                                height: 25,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                bgcolor: '#ede9fe',
                                color: '#5b21b6',
                                border: '1px solid #ddd6fe',
                                borderRadius: 1,
                              }}
                            />
                          ) : (
                            <Chip
                              label="Patient Records"
                              onClick={(e) => { e.stopPropagation(); handlePatientClick(patientInfo, apt); }}
                              size="small"
                              clickable
                              sx={{
                                height: 25,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                bgcolor: '#ede9fe',
                                color: '#5b21b6',
                                border: '1px solid #ddd6fe',
                                borderRadius: 1,
                              }}
                            />
                          )
                        )}

{apt.status === "scheduled" && activeAppointmentTab !== 'draft' && !isAnyNoShow && (
  <Button
    variant="outlined"
    size="small"
    onClick={(e) => {
      e.stopPropagation();
      handleCancelClick(apt);
    }}
    sx={{
      height: 28,
      fontSize: '0.72rem',
      fontWeight: 600,
      color: '#dc2626',
      borderColor: '#fca5a5',
      borderRadius: '6px',
      textTransform: 'none',
      px: 1.5,
      '&:hover': {
        bgcolor: '#fef2f2',
        borderColor: '#ef4444',
      },
    }}
  >
    Cancel
  </Button>
)}

{activeAppointmentTab === 'today' && apt.consultation_type?.trim().toLowerCase() === 'video' && !isCancelledApt && aptSessionEnded && !apt.customer_state?.trim() && !apt.counsellor_state?.trim() && (
  (() => {
    const isVideoOngoing = apt.customer_state === 'in-session' && apt.counsellor_state === 'in-session';
    const localStatus = audioCallStatuses[apt.id] || '';
    const showAsOngoing = isVideoOngoing || localStatus === 'call-initiated';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <FormControl size="small" onClick={(e) => e.stopPropagation()}>
          <Select
            value={showAsOngoing ? 'call-ongoing' : ''}
            onChange={(e) => handleVideoCallStatusChange(e, apt)}
            displayEmpty
            sx={{
              height: 28,
              minWidth: 160,
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '14px',
              color: '#fff',
              background: 'linear-gradient(135deg, #00b09b, #0ea5e9)',
              boxShadow: '0 2px 8px rgba(0, 176, 155, 0.3)',
              '.MuiOutlinedInput-notchedOutline': { border: 'none' },
              '.MuiSelect-icon': { color: '#fff' },
              '&:hover': { background: 'linear-gradient(135deg, #00967e, #0284c7)' },
            }}
          >
            <MenuItem value="" disabled>Update Status</MenuItem>
            {showAsOngoing && <MenuItem value="call-ongoing" disabled>Session Ongoing</MenuItem>}
            <MenuItem value="customer-not-joined">Customer Not Joined</MenuItem>
            <MenuItem value="call-ended">Session Ended</MenuItem>
          </Select>
        </FormControl>
      </Box>
    );
  })()
)}
                      

                      </Box>

                    </Box>
                  </Box>

                  {index < displayList.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Card>

      {/* Cancel Confirmation Dialog */}
<Dialog
  open={cancelDialogOpen}
  onClose={handleCancelDialogClose}
  maxWidth="xs"
  fullWidth
>
  {canCancel ? (
    <>
      <DialogTitle
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          pb: 1,
        }}
      >
        Cancel Appointment
      </DialogTitle>

      <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          Are you sure you want to cancel this appointment? This action cannot
          be undone.
        </Typography>
        <TextField
          label="Cancellation Remark"
          placeholder="Please provide a reason for cancellation…"
          multiline
          rows={3}
          fullWidth
          required
          value={cancelRemark}
          onChange={(e) => setCancelRemark(e.target.value)}
          size="small"
          sx={{ mt: 1 }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancelDialogClose}
          sx={{ minWidth: 90 }}
        >
          Back
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={handleConfirmCancel}
          disabled={!cancelRemark.trim() || isCancelling}
          sx={{ minWidth: 90 }}
        >
          {isCancelling ? 'Cancelling…' : 'Confirm'}
        </Button>
      </DialogActions>
    </>
  ) : (
    <>
      <DialogTitle
        sx={{
          fontSize: "1.25rem",
          fontWeight: 600,
          pb: 1,
        }}
      >
        Unable to Cancel Appointment
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          Appointments can only be cancelled more than{" "}
          <strong>2 hours</strong> before the scheduled start time.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={handleCancelDialogClose}
          sx={{ minWidth: 90 }}
        >
          OK
        </Button>
      </DialogActions>
    </>
  )}
</Dialog>

    </Box>
  );
};

export default AppointmentsTab;